import {
  Alert,
  AlertTitle,
  Avatar,
  Box,
  BoxProps,
  Collapse,
  Divider,
  Grid,
  GridProps,
  IconButton,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Snackbar,
  Typography,
} from '@mui/material'
import {
  PropsWithChildren,
  ReactElement,
  SyntheticEvent,
  useEffect,
  useRef,
  useState,
} from 'react' // Import MUI components

import { green, grey, red, yellow } from '@mui/material/colors' // Import MUI colors

import { Form, Formik, FormikHelpers } from 'formik' // Import Formik components
import { useTranslation } from 'react-i18next' // Import i18n translation hook
import { date, InferType, object, string } from 'yup' // Import Yup validation schema

// Document status MUI icons
import FindInPageIcon from '@mui/icons-material/FindInPage' // Review status icon
import PriorityHighIcon from '@mui/icons-material/PriorityHigh' // Pending status icon
import RunningWithErrorsIcon from '@mui/icons-material/RunningWithErrors' // Expiring and Valid status icon
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt' // Rejected and Expired status icon
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt' // Approved and Valid status icon

import CloseIcon from '@mui/icons-material/Close'
// Action MUI icons
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import { DocumentProps, SnackBarAlertType } from '../../types/interface' // Import Alert definition

import FileUpload from '../forms/FileUploadWrapper' // Import custom File upload wrapper
import MultipleUpload from '../forms/MultipleUploadWrapper' // Import custom Multiple file upload wrapper
import SelectDate from '../forms/SelectDateWrapper' // Import custom Date wrapper
import SubmitButton from '../forms/SubmitButtonWrapper' // Import custom Submit button wrapper
import Textfield from '../forms/TextfieldWrapper' // Import custom Textfield wrapper

import API from '../../api/baseUrl' // Import API
import pathUrl from '../../api/pathURL' // Import pathUrl
import { useAuthContext } from '../../auth/hooks/useAuthContext'

interface DocumentStatusType {
  disable: boolean
  icon: ReactElement
  message: null | string | undefined
  style: object
}

const customNotesMatches = ['abstract', 'safety', 'background']

const MasterDocuments = (
  props: PropsWithChildren<
    { city?: string } & Partial<DocumentProps>
  > = {}
) => {
  const auth = useAuthContext()
  const { t } = useTranslation()
  const { city, customName: custom_name, data, vehicleId: vehicle_id } = props
  const [open, setOpen] = useState<boolean>(false)
  const [alertEvent, setAlertEvent] = useState<SnackBarAlertType>({
    message: '',
    open: false,
    type: 'info',
  })
  const [status, setStatus] = useState<DocumentStatusType>({
    disable: true,
    icon: <FindInPageIcon />,
    message: 'Documents are under review.',
    style: { backgroundColor: grey[100], color: grey[500] },
  })
  const [isRecordCheckCertificate, setIsRecordCheckCertificate] =
    useState<boolean>(false)
  const componentIsMounted = useRef(true)

  const validationSchema = object().shape({
    // Dynamic formik validation schema
    key1: string()
      .nullable()
      .required(`${t('documents.validations.upload_required_doc')}`),
    ...(data?.document.has_expiration && {
      expiration_date: date()
        .nullable()
        .required(`${t('documents.validations.select_expiration_date')}`),
    }),
    ...(data?.license_number && {
      license_number: string().required(
        `${t('documents.validations.enter_license_number')}`
      ),
    }),
    ...(data?.license_type && {
      license_type: string().required(
        `${t('documents.validations.select_license')}`
      ),
    }),
    ...(data?.key2 && {
      key2: string()
        .nullable()
        .required(`${t('documents.validations.upload_required_doc')}`),
    }),
  })

  const handleSubmit = async (
    values: InferType<typeof validationSchema>,
    actions: FormikHelpers<InferType<typeof validationSchema>>
  ) => {
    const { license_number, license_type, ...rest } = values
    let body = {
      ...values,
      custom_name,
      document_id: data?.document.id,
      vehicle_id,
    }

    if (license_number && license_type) {
      body = { ...rest, document_id: data?.document.id }
    }

    try {
      const tokenSource = await auth.getAccessJwtToken() // Get access token from auth context
      const options = {
        headers: {
          ...(import.meta.env.MODE ? { env: import.meta.env.MODE! } : {}),
          Authorization: tokenSource,
        },
      }
      const response = await API.post(pathUrl.updateDocuments, body, options) // Post data to API
      const success = await response.data.success // Get success status
      if (success) {
        actions.setSubmitting(false)

        // If success
        setAlertEvent({
          message: `${t('documents.alerts.success')}`,
          open: true,
          type: 'success',
        }) // Set alert state
        setStatus({
          disable: true,
          icon: <FindInPageIcon />,
          message: `${t('documents.status.review')}`,
          style: { backgroundColor: grey[100], color: grey[500] },
        }) // Set status state
        setOpen(!open)
      }
    } catch (error) {
      console.log(error)
    }
  }

  const handleCollapse = () => {
    // Collapse state handler
    setOpen(!open)
  }

  const handleClose = (event: Event | SyntheticEvent, reason?: string) => {
    if (reason === 'clickaway') {
      return
    }
    setAlertEvent({ ...alertEvent, open: false })
  }

  const statusManager = (documentStatus: string | undefined) => {
    // Status manager
    switch (documentStatus) {
      case 'approved':
        setStatus({
          disable: true,
          icon: <ThumbUpAltIcon />,
          message: `${t('documents.status.approved')}`,
          style: { backgroundColor: green[100], color: green[500] },
        })
        break
      case 'expired':
        setStatus({
          disable: false,
          icon: <ThumbDownAltIcon />,
          message: `${t('documents.status.expired')}`,
          style: { backgroundColor: red[50], color: red[500] },
        })
        break
      case 'expiring':
        setStatus({
          disable: false,
          icon: <RunningWithErrorsIcon />,
          message: `${t('documents.status.expiring', { data })}`,
          style: { backgroundColor: yellow[100], color: yellow[900] },
        })
        break
      case 'pending':
        setStatus({
          disable: false,
          icon: <PriorityHighIcon />,
          message: `${t('documents.status.pending')}`,
          style: { backgroundColor: red[50], color: red[500] },
        })
        break
      case 'rejected':
        setStatus({
          disable: false,
          icon: <ThumbDownAltIcon />,
          message: data?.notes,
          style: { backgroundColor: red[50], color: red[500] },
        })
        break
      default:
        setStatus({
          disable: true,
          icon: <FindInPageIcon />,
          message: `${t('documents.status.review')}`,
          style: { backgroundColor: grey[100], color: grey[500] },
        })
        break
    }
  }

  const setDirectory = (
    documentType: string | undefined,
    type: 'user' | 'vehicle'
  ) => {
    let directory
    if (type === 'user') {
      // convert documentType to lowercase and replace spaces with underscores
      directory = documentType?.toLowerCase().replace(/ /g, '_')
    } else {
      // convert documentType to lowercase and replace spaces with underscores
      directory = `Vehicle/${vehicle_id}/${documentType?.toLowerCase().replace(/ /g, '_')}`
    }
    return directory
  }

  const configGridContainer: GridProps = {
    container: true,
    rowGap: 4,
    sx: {
      backgroundColor: '#FAF9F9',
      borderTopLeftRadius: 16,
      borderTopRightRadius: 16,
      mb: 2,
      p: 4,
    },
  }

  const configGridItem: GridProps = {
    item: true,
    xs: 12,
  }

  const boxConfig: BoxProps = {
    sx: {
      backgroundColor: '#FAF9F9',
      borderBottomLeftRadius: 16,
      borderBottomRightRadius: 16,
      mb: 2,
      mt: -2,
      p: 4,
    },
  }

  const initialValues = {
    // Dynamic formik initial values
    key1: '',
    ...(data?.document.has_expiration && { expiration_date: '' }),
    ...(data?.license_number && { license_number: data.license_number }),
    ...(data?.license_type && { license_type: data.license_type }),
    ...(data?.key2 && { key2: '' }),
  } as InferType<typeof validationSchema>

  const allowMultipleByTitle = [
    'City Licensing Thunder Bay',
    'Personenbeförderungsschein',
  ]
  const allowMultipleByName = ['background']

  useEffect(() => {
    if (componentIsMounted.current) {
      statusManager(data?.status)
    }
    return () => {
      componentIsMounted.current = false
    }
  }, [])

  useEffect(() => {
    if (data && data.document?.name === 'record_certificate') {
      setIsRecordCheckCertificate(true)
    }
  }, [data, isRecordCheckCertificate])

  return (
    <>
      {!isRecordCheckCertificate && (
        <ListItemButton disabled={status.disable} onClick={handleCollapse}>
          <ListItemAvatar>
            <Avatar sx={status.style} variant="circular">
              {status.icon}
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={
              <Typography color="text.primary" variant="subtitle1">
                {data?.document.title}
              </Typography>
            }
            secondary={
              <Typography color="text.secondary" variant="body2">
                {status.message}
              </Typography>
            }
          />
          {!status.disable && !open && <ExpandMoreIcon />}
          {!status.disable && open && <ExpandLessIcon />}
        </ListItemButton>
      )}
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
        >
          <Form>
            <Grid {...configGridContainer}>
              {data?.custom_notes && (
                <Grid {...configGridItem}>
                  <Alert severity="warning">
                    <AlertTitle> {t('notifications.heads_up')} </AlertTitle>
                    {customNotesMatches.includes(data.document?.name)
                      ? `Prior to completing a new ${data.document?.title}, it's best to wait to hear back from our team to ensure that you meet the initial qualifications to drive on the platform.`
                      : data?.custom_notes}
                  </Alert>
                </Grid>
              )}
              {data?.license_number && (
                <Grid {...configGridItem}>
                  <Textfield
                    disabled
                    label={t('profile.info.license_number')}
                    name="license_number"
                  />
                </Grid>
              )}
              {data?.license_type && (
                <Grid {...configGridItem}>
                  <Textfield
                    disabled
                    label={t('profile.info.license_type')}
                    name="license_type"
                  />
                </Grid>
              )}
              {data?.document.has_expiration && (
                <Grid {...configGridItem}>
                  <SelectDate
                    label={t('documents.additional_documents.expiration_date')}
                    name="expiration_date"
                  />
                </Grid>
              )}
              {data?.document.name === 'HST Number' && (
                <Grid {...configGridItem}>
                  <Textfield
                    label={t('documents.additional_documents.hst_number')}
                    name="key1"
                  />
                </Grid>
              )}
              {data?.document && (allowMultipleByTitle.includes(data.document.title) || allowMultipleByName.includes(data.document?.name)) ? (
                <Grid {...configGridItem}>
                  <MultipleUpload
                    city={city}
                    documentType="document"
                    folder={setDirectory(
                      data?.document.name,
                      data?.document?.type
                    )}
                    label={t('documents.additional_documents.document')}
                    name="key1"
                  />
                </Grid>
              ) : (
                <Grid {...configGridItem}>
                  <FileUpload
                    city={city}
                    documentType="document"
                    fileName={data?.document.name}
                    folder={setDirectory(
                      data?.document.name,
                      data?.document?.type
                    )}
                    label={
                      data?.document.name === 'driver_license'
                        ? `${t('documents.driver_license.front_label')}`
                        : `${t('documents.document')}`
                    }
                    name="key1"
                  />
                </Grid>
              )}
              {data?.key2 && (
                <Grid {...configGridItem}>
                  <FileUpload
                    city={city}
                    documentType="document"
                    fileName={data?.document.name}
                    folder={setDirectory(
                      data?.document.name,
                      data?.document?.type
                    )}
                    label={
                      data?.document.name === 'driver_license'
                        ? `${t('documents.driver_license.back_label')}`
                        : `${t('documents.document')}`
                    }
                    name="key2"
                  />
                </Grid>
              )}
            </Grid>
            <Box {...boxConfig}>
              <SubmitButton fullWidth>
                {`Upload ${data?.document.title}`}
              </SubmitButton>
            </Box>
          </Form>
        </Formik>
      </Collapse>
      <Divider />
      <Snackbar
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
        autoHideDuration={6000}
        onClose={handleClose}
        open={alertEvent.open}
      >
        <Alert
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              onClick={handleClose}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          }
          elevation={6}
          onClose={handleClose}
          severity={alertEvent.type}
          sx={{
            width: '100%',
          }}
        >
          {alertEvent.message}
        </Alert>
      </Snackbar>
    </>
  )
}

export default MasterDocuments
